import * as React from "react";
import Link from "next/link";

import type { PublicJob } from "@/lib/job-board/types";
import { Button } from "@/components/ui/button";
import RecentJobCard from "./recent-job-card";
import RecentJobsSkeleton from "./recent-jobs-skeleton";

type Props = {
  initialJobs?: PublicJob[];
  initialError?: boolean;
};

/**
 * The public list endpoint enforces publication eligibility. It currently has
 * no public publication timestamp or supported sort parameter, so this section
 * preserves the backend order and never substitutes createdAt as a date posted.
 */
export default function RecentlyPostedJobsSection({ initialJobs, initialError = false }: Props) {
  const [jobs, setJobs] = React.useState<PublicJob[]>(initialJobs || []);
  const [isLoading, setIsLoading] = React.useState(!initialJobs && !initialError);
  const [hasError, setHasError] = React.useState(initialError);

  const loadJobs = React.useCallback(async () => {
    setIsLoading(true);
    setHasError(false);
    try {
      const response = await fetch("/api/jobs?page=1&limit=6");
      if (!response.ok) throw new Error("Unable to load public jobs");
      const data: unknown = await response.json();
      const hits = data && typeof data === "object" && Array.isArray((data as { hits?: unknown }).hits)
        ? (data as { hits: PublicJob[] }).hits
        : [];
      setJobs(hits.slice(0, 6));
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (!initialJobs && !initialError) void loadJobs();
  }, [initialError, initialJobs, loadJobs]);

  return (
    <section className="container py-10" aria-labelledby="recently-posted-jobs-heading" aria-busy={isLoading || undefined}>
      <div className="mb-6 max-w-2xl">
        <p className="text-eyebrow mb-2">Latest opportunities</p>
        <h2 id="recently-posted-jobs-heading" className="mb-2">
          Explore Recently Posted Jobs
        </h2>
        <p className="m-0 text-sm text-muted-foreground sm:text-[15px]">
          Discover the latest job openings from leading employers and find your next career opportunity.
        </p>
      </div>

      {isLoading ? <RecentJobsSkeleton /> : null}

      {!isLoading && hasError ? (
        <div className="recent-job-card px-5 py-8 text-center" role="status" aria-live="polite">
          <p className="m-0 text-sm text-muted-foreground">We couldn’t load recent job openings right now.</p>
          <Button type="button" variant="outline" size="sm" className="mt-4" onClick={loadJobs}>
            Try again
          </Button>
        </div>
      ) : null}

      {!isLoading && !hasError && jobs.length === 0 ? (
        <div className="recent-job-card px-5 py-8 text-center">
          <p className="m-0 text-sm text-muted-foreground">No recent job openings available right now.</p>
          <Link href="/jobs" className="mt-4 inline-block rounded-sm text-sm font-semibold text-primary outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
            Explore all available jobs
          </Link>
        </div>
      ) : null}

      {!isLoading && !hasError && jobs.length > 0 ? (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {jobs.map((job) => <RecentJobCard key={job.id} job={job} />)}
          </div>
          <div className="mt-6 text-center">
            <Button asChild variant="outline">
              <Link href="/jobs">View All Jobs</Link>
            </Button>
          </div>
        </>
      ) : null}
    </section>
  );
}
